import { Head, Link, router, usePage } from "@inertiajs/react"
import { NativeButton, NativeNavbar, NativeTabs } from "@ruby-native/react"
import {
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Clock,
  Pencil,
  X,
} from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { type Timer } from "@/hooks/use-timers"
import { type StartTimer, useTimersContext } from "@/hooks/use-timers-context"
import { useUnitSystem } from "@/hooks/use-unit-system"
import AppLayout from "@/layouts/app-layout"
import {
  DURATION_PATTERN,
  extractSentence,
  formatSeconds,
  parseDurationToSeconds,
} from "@/lib/duration"
import { timerColor } from "@/lib/timer-colors"
import { type UnitSystem, convertMeasurement } from "@/lib/unit-convert"
import { cn } from "@/lib/utils"
import {
  cookRecipePath,
  editRecipePath,
  recipePath,
  recipesPath,
} from "@/routes"
import type { BreadcrumbItem, Recipe } from "@/types"

const UNIT_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300",
]

function unitColor(unit: string): string {
  let hash = 0
  for (let i = 0; i < unit.length; i++) {
    hash = unit.charCodeAt(i) + ((hash << 5) - hash)
  }
  return UNIT_COLORS[Math.abs(hash) % UNIT_COLORS.length]
}

function formatQuantity(quantity: number | null) {
  if (quantity == null) return null
  return parseFloat(
    Number(quantity)
      .toFixed(2)
      .replace(/\.?0+$/, ""),
  )
}

const PANTRY_STAPLES = new Set([
  "salt",
  "kosher salt",
  "sea salt",
  "pepper",
  "black pepper",
  "olive oil",
  "extra virgin olive oil",
  "vegetable oil",
  "canola oil",
  "cooking spray",
  "butter",
  "salted butter",
  "unsalted butter",
  "flour",
  "all-purpose flour",
  "sugar",
  "brown sugar",
  "powdered sugar",
  "garlic",
  "onion",
  "water",
])

function isStaple(name: string): boolean {
  const lower = name.toLowerCase().trim()
  if (PANTRY_STAPLES.has(lower)) return true
  // Check if name starts with a staple (e.g. "salt to taste", "pepper to taste")
  for (const staple of PANTRY_STAPLES) {
    if (lower.startsWith(staple)) return true
  }
  return false
}

function UnitToggle({
  system,
  onSystemChange,
}: {
  system: UnitSystem
  onSystemChange: (system: UnitSystem) => void
}) {
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      value={system}
      onValueChange={(value) => {
        if (value) onSystemChange(value as UnitSystem)
      }}
    >
      <ToggleGroupItem value="metric" className="h-8 text-xs">
        Metric
      </ToggleGroupItem>
      <ToggleGroupItem value="imperial" className="h-8 text-xs">
        Imperial
      </ToggleGroupItem>
    </ToggleGroup>
  )
}

function IngredientRow({
  ingredient,
  system,
}: {
  ingredient: Recipe["ingredients"][0]
  system: UnitSystem
}) {
  const { quantity, unit } = convertMeasurement(
    ingredient.quantity,
    ingredient.unit,
    system,
  )
  return (
    <li className="flex items-center gap-2">
      <span className="font-medium tabular-nums">
        {formatQuantity(quantity)}
      </span>
      {unit && (
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${unitColor(unit)}`}
        >
          {unit}
        </span>
      )}
      <span>{ingredient.name}</span>
    </li>
  )
}

function IngredientsList({
  ingredients,
  system,
  onSystemChange,
}: {
  ingredients: Recipe["ingredients"]
  system: UnitSystem
  onSystemChange: (system: UnitSystem) => void
}) {
  const sorted = [...ingredients].sort((a, b) => {
    const aStaple = isStaple(a.name)
    const bStaple = isStaple(b.name)
    if (aStaple === bStaple) return 0
    return aStaple ? -1 : 1
  })

  return (
    <section className="mb-8">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-xl tracking-tight">Ingredients</h2>
        <UnitToggle system={system} onSystemChange={onSystemChange} />
      </div>
      <ul className="space-y-2">
        {sorted.map((ingredient) => (
          <IngredientRow
            key={ingredient.id}
            ingredient={ingredient}
            system={system}
          />
        ))}
      </ul>
    </section>
  )
}

// Common cooking words that shouldn't match as ingredients on their own
const STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "of",
  "to",
  "in",
  "for",
  "with",
  "on",
  "up",
  "out",
  "hot",
  "low",
  "red",
  "dry",
  "fresh",
  "large",
  "small",
  "medium",
  "chopped",
  "minced",
  "diced",
  "sliced",
  "crushed",
  "ground",
  "cooked",
  "drained",
  "rinsed",
])

function buildIngredientKeywords(ingredients: Recipe["ingredients"]) {
  const keywords: { term: string; ingredient: Recipe["ingredients"][0] }[] = []

  for (const ing of ingredients) {
    // Add full name first (highest priority)
    keywords.push({ term: ing.name, ingredient: ing })

    // Add meaningful individual words / multi-word fragments
    const words = ing.name.toLowerCase().split(/\s+/)
    const meaningful = words.filter((w) => w.length > 2 && !STOP_WORDS.has(w))
    for (const word of meaningful) {
      keywords.push({ term: word, ingredient: ing })
    }
  }

  // Sort by term length descending so longer matches win ("olive oil" before "oil")
  keywords.sort((a, b) => b.term.length - a.term.length)

  // Deduplicate terms, keeping the first (longest name's) ingredient
  const seen = new Set<string>()
  return keywords.filter((k) => {
    const lower = k.term.toLowerCase()
    if (seen.has(lower)) return false
    seen.add(lower)
    return true
  })
}

function findMatchedIngredients(
  text: string,
  ingredients: Recipe["ingredients"],
) {
  const keywords = buildIngredientKeywords(ingredients)
  const seen = new Set<number>()
  const matched: Recipe["ingredients"][0][] = []

  for (const kw of keywords) {
    const pattern = new RegExp(
      `\\b${kw.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
      "i",
    )
    if (pattern.test(text) && !seen.has(kw.ingredient.id)) {
      seen.add(kw.ingredient.id)
      matched.push(kw.ingredient)
    }
  }
  return matched
}

// Wires the in-text duration chips to the timer state: where the duration lives
// (sourcePrefix + offset = sourceId), how to start it, and which color (if any)
// an active timer has assigned to that source so the chip can match it.
interface DurationTimerProps {
  sourcePrefix: string
  onStart: (
    seconds: number,
    sourceId: string,
    durationText: string,
    sentence: string,
  ) => void
  timerFor: (sourceId: string) => Timer | undefined
}

function highlightIngredients(
  text: string,
  ingredients: Recipe["ingredients"],
  system: UnitSystem,
  timer?: DurationTimerProps,
) {
  const keywords = buildIngredientKeywords(ingredients)

  // Build combined pattern: durations + ingredient keywords
  const ingredientTerms = keywords.map((k) =>
    k.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  )
  const patterns: string[] = [DURATION_PATTERN.source]
  if (ingredientTerms.length > 0) {
    patterns.push(`\\b(?:${ingredientTerms.join("|")})\\b`)
  }
  const combined = new RegExp(`(${patterns.join("|")})`, "gi")

  const parts = text.split(combined)
  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null

        // Check if it's a duration
        if (DURATION_PATTERN.test(part)) {
          DURATION_PATTERN.lastIndex = 0
          const seconds = parseDurationToSeconds(part)
          if (timer && seconds) {
            const offset = parts.slice(0, i).join("").length
            const sentence = extractSentence(text, offset, offset + part.length)
            const sourceId = `${timer.sourcePrefix}:${offset}`
            const active = timer.timerFor(sourceId)
            const colors = active ? timerColor(active.colorIndex) : null
            return (
              <button
                key={i}
                type="button"
                onClick={() => timer.onStart(seconds, sourceId, part, sentence)}
                title={
                  active ? `Timer running for ${part}` : `Start a ${part} timer`
                }
                className={cn(
                  "inline-flex cursor-pointer items-center gap-1 rounded px-1 font-semibold transition-colors",
                  colors
                    ? colors.chip
                    : "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/50 dark:text-amber-300 dark:hover:bg-amber-900",
                )}
              >
                <Clock className="inline size-[0.85em]" />
                {part}
                {active && (
                  <span className="ml-0.5 font-normal tabular-nums opacity-80">
                    {active.status === "done"
                      ? "done"
                      : formatSeconds(active.remainingSeconds)}
                  </span>
                )}
              </button>
            )
          }
          return (
            <span
              key={i}
              className="inline-flex cursor-default items-center gap-1 rounded bg-amber-100 px-1 font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
            >
              <Clock className="inline size-[0.85em]" />
              {part}
            </span>
          )
        }

        // Check if it's an ingredient
        const match = keywords.find(
          (k) => k.term.toLowerCase() === part.toLowerCase(),
        )
        if (match) {
          const ing = match.ingredient
          const { quantity, unit } = convertMeasurement(
            ing.quantity,
            ing.unit,
            system,
          )
          const qty = formatQuantity(quantity)
          const label = [qty, unit, ing.name].filter(Boolean).join(" ")

          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <span className="bg-primary/10 text-primary cursor-default rounded px-1 font-medium">
                  {part}
                </span>
              </TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          )
        }

        return <span key={i}>{part}</span>
      })}
    </>
  )
}

interface CookingModeProps {
  recipe: Recipe
  initialStep: number
  system: UnitSystem
  onStartTimer: StartTimer
  timerFor: (sourceId: string) => Timer | undefined
}

function CookingMode({
  recipe,
  initialStep,
  system,
  onStartTimer,
  timerFor,
}: CookingModeProps) {
  const totalSteps = recipe.instructions.length
  // 0 = ingredients, 1..n = instructions
  const [step, setStep] = useState(() =>
    Math.min(Math.max(0, initialStep), totalSteps),
  )

  const prev = useCallback(() => setStep((s) => Math.max(0, s - 1)), [])
  const next = useCallback(
    () => setStep((s) => Math.min(totalSteps, s + 1)),
    [totalSteps],
  )
  const onClose = useCallback(
    () => router.visit(recipePath({ id: recipe.slug })),
    [recipe.slug],
  )

  // Keep the step in the URL so the view can be shared and survives a refresh,
  // without a server round-trip on every step.
  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set("step", String(step))
    window.history.replaceState(window.history.state, "", url)
  }, [step])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") prev()
      else if (e.key === "ArrowRight") next()
      else if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [prev, next, onClose])

  const { nativeApp } = usePage().props

  // On a native phone we render inside the app's own chrome: a NativeNavbar
  // (with a Done button, since Normal Mode has no system back button) and a
  // normally-flowing page so the native web view handles scrolling. The web
  // build keeps the full-screen fixed overlay, which the iOS web view will not
  // scroll reliably.
  return (
    <div
      className={
        nativeApp
          ? "flex min-h-screen flex-col"
          : "bg-background fixed inset-0 z-50 flex flex-col"
      }
    >
      {/* Render the tab bar so the native shell fills its reserved space
          instead of leaving an empty gap below the cooking view. */}
      {nativeApp && <NativeTabs />}
      {/* Header */}
      {nativeApp ? (
        <NativeNavbar title={recipe.title} pullToRefresh={false}>
          <NativeButton
            position="trailing"
            title="Done"
            href={recipePath({ id: recipe.slug })}
          />
        </NativeNavbar>
      ) : (
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h1 className="text-xl tracking-tight">{recipe.title}</h1>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>
      )}

      {/* Content */}
      <div
        className={
          nativeApp
            ? "flex-1 px-6 pb-[calc(2rem_+_var(--timers-drawer-space,0px))] pt-[calc(env(safe-area-inset-top)_+_1.5rem)]"
            : "flex-1 overflow-y-auto px-6 pt-8 pb-[calc(2rem_+_var(--timers-drawer-space,0px))]"
        }
      >
        <div className="mx-auto flex min-h-full w-full max-w-2xl flex-col">
          <div className="m-auto w-full">
          {step === 0 ? (
            <div>
              <h2 className="mb-6 text-center text-2xl tracking-tight">
                Ingredients
              </h2>
              <ul className="space-y-3">
                {recipe.ingredients.map((ingredient) => {
                  const { quantity, unit } = convertMeasurement(
                    ingredient.quantity,
                    ingredient.unit,
                    system,
                  )
                  return (
                    <li
                      key={ingredient.id}
                      className="flex items-center gap-3 text-lg"
                    >
                      <span className="font-medium tabular-nums">
                        {formatQuantity(quantity)}
                      </span>
                      {unit && (
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-sm font-medium ${unitColor(unit)}`}
                        >
                          {unit}
                        </span>
                      )}
                      <span>{ingredient.name}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : (
            (() => {
              const instruction = recipe.instructions[step - 1]
              const matched = findMatchedIngredients(
                instruction.body,
                recipe.ingredients,
              )
              return (
                <div className="text-center">
                  <p className="text-muted-foreground mb-4 text-sm font-medium">
                    Step {step} of {totalSteps}
                  </p>
                  <div className="bg-primary text-primary-foreground mx-auto mb-8 flex size-14 items-center justify-center rounded-full text-2xl font-medium">
                    {step}
                  </div>
                  <p className="text-left text-2xl leading-relaxed text-pretty">
                    {highlightIngredients(
                      instruction.body,
                      recipe.ingredients,
                      system,
                      {
                        sourcePrefix: `i${instruction.id}`,
                        timerFor,
                        onStart: (seconds, sourceId, durationText, sentence) =>
                          onStartTimer({
                            seconds,
                            label: durationText,
                            context: sentence,
                            sourceId,
                            step,
                            recipeId: recipe.id,
                            recipeTitle: recipe.title,
                          }),
                      },
                    )}
                  </p>
                  {matched.length > 0 && (
                    <div className="bg-muted/50 mx-auto mt-8 max-w-md rounded-lg p-4 text-left">
                      <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                        You'll need
                      </p>
                      <ul className="space-y-1.5">
                        {matched.map((ing) => {
                          const { quantity, unit } = convertMeasurement(
                            ing.quantity,
                            ing.unit,
                            system,
                          )
                          return (
                            <li
                              key={ing.id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <span className="font-medium tabular-nums">
                                {formatQuantity(quantity)}
                              </span>
                              {unit && (
                                <span
                                  className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${unitColor(unit)}`}
                                >
                                  {unit}
                                </span>
                              )}
                              <span>{ing.name}</span>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )
            })()
          )}
          </div>
        </div>
      </div>

      {/* Footer navigation */}
      <div
        className={
          nativeApp
            ? "bg-background sticky bottom-0 flex items-center justify-between border-t px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)_+_1rem)]"
            : "flex items-center justify-between border-t px-6 py-4"
        }
      >
        {nativeApp && step === 0 ? (
          <Button variant="outline" onClick={onClose} className="gap-2">
            <X className="size-4" />
            Close
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={prev}
            disabled={step === 0}
            className="gap-2"
          >
            <ChevronLeft className="size-4" />
            Previous
          </Button>
        )}

        <span className="text-muted-foreground text-sm tabular-nums">
          {step === 0 ? "Ingredients" : `${step} / ${totalSteps}`}
        </span>

        {step < totalSteps ? (
          <Button onClick={next} className="gap-2">
            Next
            <ChevronRight className="size-4" />
          </Button>
        ) : (
          <Button onClick={onClose} className="gap-2">
            Done
          </Button>
        )}
      </div>
    </div>
  )
}

interface Props {
  recipe: Recipe
  cook?: boolean
  step?: number
}

export default function Show({ recipe, cook = false, step = 0 }: Props) {
  // Timer state lives in the persistent layout so timers survive navigation;
  // this page only starts timers and tints chips to match running ones.
  const { startTimer, timerFor } = useTimersContext()
  const [unitSystem, setUnitSystem] = useUnitSystem()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Recipes", href: recipesPath() },
    { title: recipe.title, href: recipePath({ id: recipe.slug }) },
  ]

  if (cook) {
    return (
      <>
        <Head title={`Cooking: ${recipe.title}`} />
        <CookingMode
          recipe={recipe}
          initialStep={step}
          system={unitSystem}
          onStartTimer={startTimer}
          timerFor={timerFor}
        />
      </>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <NativeNavbar title={recipe.title} pullToRefresh={false} />
      <Head title={recipe.title} />

      <div className="mx-auto max-w-5xl p-4 pb-16">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl tracking-tight text-balance">
            {recipe.title}
          </h1>
          <div className="flex shrink-0 gap-2">
            {recipe.instructions.length > 0 && (
              <Button size="sm" asChild>
                <Link href={cookRecipePath({ id: recipe.slug })}>
                  <ChefHat className="mr-2 h-4 w-4" />
                  Let's cook!
                </Link>
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link href={editRecipePath({ id: recipe.slug })}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          </div>
        </div>

        {recipe.cover_image_url && (
          <img
            src={recipe.cover_image_url}
            alt={recipe.title}
            className="mb-6 aspect-video w-full rounded-lg object-cover"
          />
        )}

        <div className="mx-auto max-w-2xl">
          {recipe.source_url && (
            <p className="text-muted-foreground mb-4 text-sm">
              Imported from{" "}
              <a
                href={recipe.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:no-underline"
              >
                {new URL(recipe.source_url).hostname}
              </a>
            </p>
          )}

          {recipe.description && (
            <p className="text-muted-foreground mb-8 text-lg text-pretty">
              {recipe.description}
            </p>
          )}

          {recipe.ingredients.length > 0 && (
            <IngredientsList
              ingredients={recipe.ingredients}
              system={unitSystem}
              onSystemChange={setUnitSystem}
            />
          )}

          {recipe.instructions.length > 0 && (
            <section className="mt-8">
              <h2 className="mb-4 text-xl tracking-tight">Instructions</h2>
              <ol className="space-y-4">
                {recipe.instructions.map((instruction, index) => (
                  <li key={instruction.id} className="flex gap-4">
                    <span className="bg-primary text-primary-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium">
                      {index + 1}
                    </span>
                    <p className="pt-0.5">
                      {highlightIngredients(
                        instruction.body,
                        recipe.ingredients,
                        unitSystem,
                        {
                          sourcePrefix: `i${instruction.id}`,
                          timerFor,
                          onStart: (
                            seconds,
                            sourceId,
                            durationText,
                            sentence,
                          ) =>
                            startTimer({
                              seconds,
                              label: durationText,
                              context: sentence,
                              sourceId,
                              step: index + 1,
                              recipeId: recipe.id,
                              recipeTitle: recipe.title,
                            }),
                        },
                      )}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
